import type {
  InterestEngineType,
  InterestLevel,
  InterestStage,
  PromoEventType,
  PromoInterestKey,
  SegmentSpec,
  WashpassStatus,
} from '@kawai/shared';
// type-only import で循環回避。customerStats.ts は本ファイルから
// INTEREST_KEY_OPTIONS / SEGMENT_EVENT_TYPE_OPTIONS を値 import しているため、
// ここを値 import にすると実行時の循環参照になる（type-only なら型消去で循環しない）。
import type { ReactionActionKey } from './customerStats.js';
import type { UserCouponActivity } from './couponStats.js';
import { describeCouponHolding } from './couponSegmentOptions.js';
import { describeFirstSource } from './firstSource.js';

// shared の純関数をそのまま再エクスポート（薄いファサード）。
// tenant-promo のページ / コンポーネントは @kawai/shared を直接 import せず
// この lib 経由で参照することで、import パスとラベル定数を 1 箇所に集約する。
export {
  matchesSegment,
  filterUsersBySegment,
  countUsersBySegment,
  isEmptySegment,
  interestStrength,
} from '@kawai/shared';

/**
 * 行動アクションタグキー → 日本語ラベル（M-59 / 旧「反応のみ」区分の細分化）。
 * 顧客一覧フィルター / 分析サマリー / 顧客詳細バッジで共用する。`customerStats.ts` の
 * `ReactionActionKey` と対応（`reactionActionsOf` が導出）。
 */
export const REACTION_ACTION_LABELS: Readonly<Record<ReactionActionKey, string>> = {
  coupon_redeem: 'クーポン利用',
  coupon_claim: 'クーポン取得',
  survey_answer: 'アンケート回答',
  richmenu_tap: 'リッチメニュー反応',
  liff_link_tap: '配信リンク反応',
  liff_visit: 'LIFF来訪のみ',
  none: 'その他',
} as const;

/** 興味キー → 日本語ラベル（SegmentFilterBar / 顧客詳細の興味タグ表示で共用）。 */
export const INTEREST_KEY_LABELS: Readonly<Record<PromoInterestKey, string>> = {
  car_sales: '車販売・買取',
  inspection: '車検',
  keeper: 'KeePer コーティング',
  washpass: 'WashPass（洗車サブスク）',
  fuel: '給油（燃料）',
} as const;

/** SegmentFilterBar の興味キー select 用（順序固定）。 */
export const INTEREST_KEY_OPTIONS: ReadonlyArray<{
  value: PromoInterestKey;
  label: string;
}> = (
  ['car_sales', 'inspection', 'keeper', 'washpass', 'fuel'] as PromoInterestKey[]
).map((value) => ({ value, label: INTEREST_KEY_LABELS[value] }));

/** M-74: エンジンタイプ → 日本語ラベル（興味タグ設定モーダルの engine セクションで使用）。 */
export const ENGINE_TYPE_LABELS: Readonly<Record<InterestEngineType, string>> = {
  periodic: '①周期実施型',
  considered: '②長期検討型',
  frequent: '③高頻度継続型',
} as const;

/** 興味確度レベル → 日本語ラベル。 */
export const INTEREST_LEVEL_LABELS: Readonly<Record<InterestLevel, string>> = {
  estimated: '推定（行動ログ）',
  self_declared: '申告',
  confirmed: '確定（予約完了など）',
} as const;

/** minLevel select 用（弱い順）。 */
export const INTEREST_LEVEL_OPTIONS: ReadonlyArray<{
  value: InterestLevel;
  label: string;
}> = (['estimated', 'self_declared', 'confirmed'] as InterestLevel[]).map((value) => ({
  value,
  label: INTEREST_LEVEL_LABELS[value],
}));

/**
 * ステージ（AIDA 4 段階 / M-72）→ 日本語ラベル。
 *
 * ⚠ **番号は飾りではなく順序の情報。** 初版は `認知（A）` のように AIDA の頭文字を添えていたが、
 * AIDA を知らない読者には**順序が読めない**（`Act` だけ 3 文字で不揃いでもある）。丸数字を経て、
 * 軸の名前ごと書く `ステージN（…）` に落ち着いた（M-115）。**単独で読んでも何の軸の話か分かる**のが
 * 丸数字との違いで、顧客一覧やセグメント条件のように前後の文脈が無い場所で効く。
 *
 * ⚠ **他の画面の文章にラベルの中身を書き写さないこと。** M-115 で表記を変えたとき、
 * アンケート編集の説明文に旧表記（丸数字）を直書きしていたぶんだけが取り残された。
 * 参照が要るなら**この定数を読む**か、`ステージ` とだけ書いて中身に触れない。
 *
 * ⚠ **このラベルは inline SVG の図（engine-guide）へ文字列として埋め込まれ、箱の幅は手で
 * 組んである。** 長さを変えたら図の実測が要る（`diagramBounds.test.tsx` が推定幅で検出するが、
 * 半透明の帯・罫線との交差までは見ないので実ブラウザも要る / M-112 の実例）。
 * 現在 8.6em 相当。これ以上伸ばすと `StageFlowDiagram` のノード箱（幅 120）に収まらない。
 */
export const INTEREST_STAGE_LABELS: Readonly<Record<InterestStage, string>> = {
  attention: 'ステージ1（認知）',
  interest: 'ステージ2（興味）',
  desire: 'ステージ3（欲求）',
  action: 'ステージ4（行動）',
} as const;

/**
 * minStage select 用（M-72 / 弱い順）。`attention` は全員一致 = 指定なしと等価のため
 * 選択肢に出さない（「指定なし」オプションが兼ねる）。
 */
export const SEGMENT_MIN_STAGE_OPTIONS: ReadonlyArray<{
  value: InterestStage;
  label: string;
}> = (['interest', 'desire', 'action'] as InterestStage[]).map((value) => ({
  value,
  label: INTEREST_STAGE_LABELS[value],
}));

/** WashPass 状態 → 日本語ラベル。 */
export const WASHPASS_STATUS_LABELS: Readonly<Record<WashpassStatus, string>> = {
  none: '未加入',
  active: '加入中',
  lapsed: '失効',
} as const;

/** WashPass チェックボックス用（順序固定）。 */
export const WASHPASS_STATUS_OPTIONS: ReadonlyArray<{
  value: WashpassStatus;
  label: string;
}> = (['none', 'active', 'lapsed'] as WashpassStatus[]).map((value) => ({
  value,
  label: WASHPASS_STATUS_LABELS[value],
}));

/** 行動ログのイベント種別 → 日本語ラベル（PromoEventTimeline で使用）。 */
export const EVENT_TYPE_LABELS: Readonly<Record<PromoEventType, string>> = {
  liff_link_tap: 'リンクタップ',
  richmenu_tap: 'リッチメニュータップ',
  liff_visit: 'LIFF 来訪',
  reservation_completed: '予約完了',
  message_sent: '配信送信',
  coupon_claim: 'クーポン取得',
  coupon_redeem: 'クーポン利用',
  survey_answer: 'アンケート回答',
  booking_page_reach: '予約ページ到達',
  // M-75: 日次バッチの機械イベント（SEGMENT_EVENT_TYPE_OPTIONS / EDITABLE_EVENT_TYPES
  // には出さない — ユーザー行動ではないため。message_sent と同じ方針）。
  cycle_reset: '周期再セット（自動）',
  // M-76: 担当者が記録した成約（HOT 案件のクローズ）。顧客の行動ログではなく管理操作の
  // 結果なので、上 2 つと同じくセグメント条件 / 確度設定の選択肢には出さない。
  hot_won: 'HOT案件 成約',
  // M-87: LINE 友だち追加（webhook）。母集団編入だけが目的で確度・ステージに寄与しないため、
  // 上 2 つと同じく SEGMENT_EVENT_TYPE_OPTIONS / EDITABLE_EVENT_TYPES には出さない
  // （顧客詳細のタイムラインでは「友だち追加」として起点が見える）。
  follow: '友だち追加',
  // M-142: ステージ昇格の通知イベント（server 専用 / シナリオ配信の起点）。顧客の行動では
  // ないので、上 3 つと同じく SEGMENT_EVENT_TYPE_OPTIONS / EDITABLE_EVENT_TYPES には出さない。
  stage_changed: 'ステージ昇格（自動）',
  // M-164: イベント抽選（ルーレット）。server 専用（lotteryDraw のみ記録）。確度に寄与しないので
  // SEGMENT_EVENT_TYPE_OPTIONS / EDITABLE_EVENT_TYPES には出さない。
  lottery_draw: 'イベント抽選',
} as const;

/**
 * リッチメニュー項目キー（promo_events.payload.item）→ 日本語ラベル（M-38 follow）。
 * 全ボタン中継方式で richmenu_tap のボタン種別を行動ログに表示するために使う。
 * ここに無いキーはキーをそのまま表示する。
 */
export const RICHMENU_ITEM_LABELS: Readonly<Record<string, string>> = {
  reservation: '各種予約',
  wash_reservation: '洗車予約',
  inspection: '車検',
  route: '経路案内',
  fuel_price: 'ガソリン価格',
  fuel_discount: '給油値引き',
  keeper: 'KeePer メニュー',
  washfree_about: '洗車機の使い方',
  washfree_top: '洗い放題',
  atwash: '洗車機を使う',
} as const;

/**
 * SegmentFilterBar の「行動ログ種別」select 用（M-37 / hasEventType）。
 * 顧客の行動を表すイベントのみ提示する（`message_sent` は配信側の記録のため除外）。
 */
export const SEGMENT_EVENT_TYPE_OPTIONS: ReadonlyArray<{
  value: PromoEventType;
  label: string;
}> = (
  [
    'richmenu_tap',
    'liff_link_tap',
    'liff_visit',
    'booking_page_reach',
    'survey_answer',
    'reservation_completed',
    'coupon_claim',
    'coupon_redeem',
  ] as PromoEventType[]
).map((value) => ({ value, label: EVENT_TYPE_LABELS[value] }));

/** JST ISO8601 → `MM/DD`（バッジの日付プレフィックス用 / pure / TZ 変換なし）。 */
function formatMonthDay(iso: string): string {
  return `${iso.slice(5, 7)}/${iso.slice(8, 10)}`;
}

/**
 * 行動アクションバッジの表示ラベルを組み立てる（pure / M-68）。
 *
 * クーポン取得・利用は、クーポンの種類にかかわらない**直近日時**が分かる場合に
 * `MM/DD` を前置する（例: `07/16クーポン利用` / `07/01クーポン取得`）。
 * 日時不明（旧 doc の欠落 / usage 未突合）や他のキーは従来ラベルのまま。
 */
export function reactionActionLabel(
  key: ReactionActionKey,
  coupon?: UserCouponActivity,
): string {
  if (key === 'coupon_redeem' && coupon?.lastRedeemedAt) {
    return `${formatMonthDay(coupon.lastRedeemedAt)}${REACTION_ACTION_LABELS[key]}`;
  }
  if (key === 'coupon_claim' && coupon?.lastClaimedAt) {
    return `${formatMonthDay(coupon.lastClaimedAt)}${REACTION_ACTION_LABELS[key]}`;
  }
  return REACTION_ACTION_LABELS[key];
}

/**
 * 行動アクションバッジの色クラス（M-68 FB / pure）。
 * 「クーポン利用」（実店舗での使用 = 最も強い反応）だけ rose で強調し、他は従来の
 * amber。形状・サイズ（rounded / px / text-[10px] 等）は呼び出し側が持つ。
 */
export function reactionBadgeColorClass(key: ReactionActionKey): string {
  // S-134 v2: 状態色トークンで塗る（生パレット禁止）。利用 = 良い側（success）、
  // 自己申告（アンケート回答）= ブランド面（brand）、行動推定系（取得・タップ）= 注意側（warning）。
  if (key === 'coupon_redeem') return 'bg-success-tint text-success-ink';
  // M-73 仕上げ: 自己申告（アンケート回答）は行動推定系と色を分ける。
  if (key === 'survey_answer') return 'bg-brand-tint text-brand-ink';
  return 'bg-warning-tint text-warning-ink';
}

/**
 * セグメント条件を人間可読な短文に要約（pure）。
 * 配信履歴（CampaignsPage）と顧客一覧の折りたたみ要約（CustomersListPage）で共用。
 * 条件未指定なら「（条件なし）」を返す。
 */
export function summarizeSegment(segment: SegmentSpec): string {
  const parts: string[] = [];
  if (segment.interestKey) {
    const lvl = segment.minLevel ? `（${segment.minLevel}以上）` : '';
    parts.push(`興味=${INTEREST_KEY_LABELS[segment.interestKey]}${lvl}`);
    if (segment.minStage) {
      // ラベル自体が「ステージN（…）」なので、ここで「ステージ=」を足すと重複する。
      parts.push(`${INTEREST_STAGE_LABELS[segment.minStage]} 以上`);
    }
    // M-75: タイプ③離反検知（評価時判定）。
    if (segment.churnRisk === true) {
      parts.push('呼び戻し対象');
    }
  }
  if (segment.washpassStatus && segment.washpassStatus.length > 0) {
    parts.push(
      `WashPass=${segment.washpassStatus
        .map((s) => WASHPASS_STATUS_LABELS[s])
        .join('/')}`,
    );
  }
  if (segment.promoTag) parts.push(`タグ=${segment.promoTag}`);
  if (typeof segment.inactiveSinceDays === 'number') {
    parts.push(`休眠${segment.inactiveSinceDays}日以上`);
  }
  if (typeof segment.inspectionDueWithinDays === 'number') {
    parts.push(`車検${segment.inspectionDueWithinDays}日以内`);
  }
  // M-166: クーポンの保有状態（label は保存時の表記 / 無ければ枚数）。
  const coupon = describeCouponHolding(segment.couponHolding);
  if (coupon) parts.push(coupon);
  // M-165: 流入経路（最初の入口）。
  const firstSource = describeFirstSource(segment);
  if (firstSource) parts.push(firstSource);
  return parts.length > 0 ? parts.join(' / ') : '（条件なし）';
}

/** 配信時刻の JST ISO8601 を `YYYY-MM-DD HH:mm` に整形（pure / TZ 変換なし）。 */
function formatCampaignTimeShort(iso: string): string {
  const datePart = iso.slice(0, 10);
  const timePart = iso.slice(11, 16);
  return timePart ? `${datePart} ${timePart}` : datePart;
}

/**
 * 行動ログの「配信」ラベルを組み立てる（pure / PromoEventTimeline で使用）。
 *
 * 対応する配信（promo_campaigns）が引ければ **配信時刻 + 管理用タイトル** を返す:
 *  - 管理用タイトルあり: `6月キャンペーン（2026-06-03 08:30）`
 *  - 管理用タイトルなし: `2026-06-03 08:30`（配信時刻のみ）
 * 引けない場合（OA 手動 broadcast / 削除済み campaign 等）は `null` を返し、行動ログには
 * 配信ラベルを出さない（生の campaignId = 配信コードは表示価値が低いため出さない）。
 */
export function formatCampaignLabel(
  campaign: { adminTitle?: string; createdAt: string } | undefined,
): string | null {
  if (!campaign) return null;
  const time = formatCampaignTimeShort(campaign.createdAt);
  if (campaign.adminTitle) {
    return `${campaign.adminTitle}（${time}）`;
  }
  return time;
}
